import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { RootState } from "@store/store";
import Modal from "@components/common/Modal/Modal";
import Button from "@components/common/Button/Button";
import { useTranslation } from "react-i18next";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CircularProgress from "@mui/material/CircularProgress";
import { authApi } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { USER_ROLES } from "@constants/user.roles";

interface CurrentSession {
	id: number;
	uuid: string;
	startDate: string;
	endDate: string;
	startYear: number;
	endYear: number;
	isActive: boolean;
	registrationStartDate: string | null;
	registrationEndDate: string | null;
}

interface CurrentAcademicCalendarResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		currentSession: CurrentSession | null;
		nextSession: CurrentSession | null;
		deadline: string | null;
		title: string | null;
		description: string | null;
		isDeadlineExpired: boolean | null;
	};
}

const SESSION_CHECK_KEY = "session_status_checked";

const SessionStatusChecker: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Get user from Redux
	const user = useSelector((state: RootState) => state.user.user);
	const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);

	// Check if user is director_general (handle both string and object formats)
	const isDirectorGeneral = user?.roles?.some((role: string | { name?: string }) => {
		if (typeof role === "string") {
			return role === USER_ROLES.DIRECTOR_GENERAL || role === USER_ROLES.DIRECTOR_GENERAL;
		}
		return role?.name === USER_ROLES.DIRECTOR_GENERAL || role?.name === USER_ROLES.DIRECTOR_GENERAL;
	});

	// Check if we've already shown the modal in this session
	const hasCheckedSession = sessionStorage.getItem(SESSION_CHECK_KEY) === "true";

	// Determine if we should fetch
	const shouldFetch = isAuthenticated && isDirectorGeneral && !hasCheckedSession;

	// Fetch current academic calendar
	const { data, isLoading, isSuccess } = useQuery<CurrentAcademicCalendarResponse, Error>({
		queryKey: ["current-academic-calendar"],
		queryFn: async (): Promise<CurrentAcademicCalendarResponse> => {
			const response = await authApi.get<CurrentAcademicCalendarResponse>(
				SERVER_ROUTES.INSTITUTE.GET_CURRENT_ACADEMIC_CALENDAR,
			);
			return response as unknown as CurrentAcademicCalendarResponse;
		},
		enabled: shouldFetch,
		staleTime: Infinity,
		retry: false,
	});

	const sessionData = data?.data;

	// Show modal when data is loaded
	useEffect(() => {
		if (isSuccess && shouldFetch) {
			setIsModalOpen(true);
			sessionStorage.setItem(SESSION_CHECK_KEY, "true");
		}
	}, [isSuccess, shouldFetch]);

	const handleClose = () => {
		setIsModalOpen(false);
		navigate(BROWSER_ROUTES.CALENDAR);
	};

	// Format date for display
	const formatDate = (dateString: string | null) => {
		if (!dateString) return null;
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	// Don't render anything if not director general or already checked
	if (!shouldFetch && !isModalOpen) {
		return null;
	}

	return (
		<Modal
			isOpen={isModalOpen}
			onClose={handleClose}
			showCloseIcon={true}
			title={t("feature.calendar.sessionStatus.modalTitle")}
			width="max-w-xl"
			height="auto"
			backgroundColor="bg-white">
			<div className="overflow-hidden">
				{isLoading ? (
					<div className="py-12 flex flex-col items-center gap-4">
						<CircularProgress size={40} />
						<p className="text-gray-600">{t("psdaCommon.common.loading")}</p>
					</div>
				) : (
					<>
						{/* Header */}
						<div className="px-6 pt-6 pb-4 pr-12">
							{sessionData?.title && <h2 className="text-xl font-semibold text-gray-900">{sessionData.title}</h2>}
							{sessionData?.description && <p className="text-sm text-gray-500 mt-1">{sessionData.description}</p>}
						</div>

						{/* Content */}
						<div className="px-6 space-y-4">
							{/* Session Info Grid */}
							<div className="grid grid-cols-1 gap-3">
								{/* Next Session */}
								{sessionData?.nextSession && (
									<div className="flex items-center gap-3 p-3 bg-white rounded-lg">
										<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
											<EventAvailableIcon className="text-blue-600" sx={{ fontSize: 20 }} />
										</div>
										<div>
											<p className="text-xs text-gray-500 uppercase tracking-wide">
												{t("feature.calendar.sessionStatus.nextSession")}
											</p>
											<p className="font-medium text-gray-900">
												{sessionData.nextSession.startYear} - {sessionData.nextSession.endYear}
											</p>
										</div>
									</div>
								)}

								{/* Deadline */}
								{sessionData?.deadline && (
									<div className="flex items-center gap-3 p-3 bg-white rounded-lg">
										<div
											className={`w-10 h-10 rounded-lg flex items-center justify-center ${
												sessionData.isDeadlineExpired ? "bg-red-100" : "bg-amber-100"
											}`}>
											<ScheduleIcon
												className={sessionData.isDeadlineExpired ? "text-red-600" : "text-amber-600"}
												sx={{ fontSize: 20 }}
											/>
										</div>
										<div>
											<p className="text-xs text-gray-500 uppercase tracking-wide">
												{t("feature.calendar.sessionStatus.deadline")}
											</p>
											<p className={`font-medium ${sessionData.isDeadlineExpired ? "text-red-600" : "text-gray-900"}`}>
												{formatDate(sessionData.deadline)}
												{sessionData.isDeadlineExpired && (
													<span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
														{t("feature.calendar.sessionStatus.expired")}
													</span>
												)}
											</p>
										</div>
									</div>
								)}

								{/* Deadline Expired Status (when no deadline but expired flag is true) */}
								{sessionData?.isDeadlineExpired === true && !sessionData?.deadline && (
									<div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
										<div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
											<ScheduleIcon className="text-red-600" sx={{ fontSize: 20 }} />
										</div>
										<div>
											<p className="font-medium text-red-700">{t("feature.calendar.sessionStatus.deadlineExpired")}</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4  border-t flex justify-end mt-4">
							<Button variant="primary" onClick={handleClose}>
								{t("feature.calendar.sessionStatus.goToCalendar")}
							</Button>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};

export default SessionStatusChecker;
